import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, RefreshCw, Search, ChevronLeft, Settings, Zap } from "lucide-react";
import PrivateLayout from "../components/PrivateLayout";
import { useAuth } from "../context/AuthContext";
import { api, formatApiError } from "../services/api";

/**
 * AdminEscrow (V3.17)
 * ────────────────────
 * Tableau de bord des séquestres pour Super Admin.
 * KPIs, filtres statut, recherche, liste enrichie (client + talent + montant + statut + date).
 * Config MoMo accessible via panneau expansible.
 */
const STATUS_LABELS = {
  PENDING_PAYMENT: "En attente dépôt",
  A_VERIFIER: "À vérifier",
  HELD_IN_ESCROW: "Sécurisé",
  IN_PROGRESS: "En cours",
  DELIVERED: "Livré",
  UNDER_REVIEW: "En validation",
  DISPUTED: "Litige",
  RELEASED: "Payé",
  COMPLETED: "Clôturé",
};

export default function AdminEscrow() {
  const { user } = useAuth();
  const isAdmin = ["admin", "super-admin"].includes(user?.role);
  const [items, setItems] = useState([]);
  const [counts, setCounts] = useState({});
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState("");
  const [q, setQ] = useState("");
  const [cfg, setCfg] = useState(null);
  const [showCfg, setShowCfg] = useState(false);
  const [running, setRunning] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const url = "/v1/admin/escrow" + (status ? `?status=${status}` : "");
      const [{ data }, { data: c }] = await Promise.all([
        api.get(url),
        api.get("/v1/admin/escrow/config"),
      ]);
      setItems(data.items || []);
      setCounts(data.counts || {});
      setCfg(c.config);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const runAutoRelease = async () => {
    setRunning(true);
    try {
      const { data } = await api.post("/v1/admin/escrow/auto-release-run");
      toast.success(`Auto-libération : ${data.released} escrow(s) libéré(s)`);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setRunning(false);
    }
  };

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return items;
    return items.filter((it) =>
      (it.id + " " + (it.company_name || "") + " " + (it.talent_name || "") + " " + (it.title || "")).toLowerCase().includes(needle)
    );
  }, [items, q]);

  if (!isAdmin) {
    return (
      <PrivateLayout title="Séquestres AFRICTAL">
        <div className="afr-empty" data-testid="admin-escrow-forbidden">
          <div className="afr-empty__title">Réservé aux administrateurs</div>
        </div>
      </PrivateLayout>
    );
  }

  const disputed = counts.DISPUTED || 0;

  return (
    <PrivateLayout
      title="🛡️ Séquestres AFRICTAL"
      breadcrumbs={[{ to: "/admin", label: "Admin" }, { label: "Séquestres" }]}
      actions={
        <div style={{ display: "flex", gap: 6 }}>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={load} data-testid="admin-escrow-refresh">
            <RefreshCw size={12} /> Actualiser
          </button>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={runAutoRelease} disabled={running}
                  data-testid="admin-escrow-auto-release">
            <Zap size={12} /> {running ? "…" : "Auto-libérer"}
          </button>
          <button className="afr-btn afr-btn--outline afr-btn--sm" onClick={() => setShowCfg((s) => !s)}
                  data-testid="admin-escrow-toggle-config">
            <Settings size={12} /> Config MoMo
          </button>
          <Link to="/admin/command" className="afr-btn afr-btn--outline afr-btn--sm">
            <ChevronLeft size={14} /> Admin
          </Link>
        </div>
      }
    >
      <div data-testid="admin-escrow-page">
        {disputed > 0 && (
          <div className="afr-card" data-testid="admin-escrow-dispute-banner"
               style={{ padding: 14, background: "#FEE2E2", color: "#991B1B", marginBottom: 12, borderLeft: "4px solid #991B1B" }}>
            <strong>⚠ {disputed} litige(s) en attente d'arbitrage.</strong> Cliquez sur un séquestre en statut « Litige » pour arbitrer.
          </div>
        )}

        {showCfg && cfg && <ConfigPanel cfg={cfg} onSaved={load} />}

        {/* KPIs */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(140px,1fr))", gap: 10, marginBottom: 14 }}
             data-testid="admin-escrow-kpis">
          {Object.entries(STATUS_LABELS).map(([code, label]) => (
            <Kpi key={code} label={label} value={counts[code] || 0} status={code} testid={`admin-escrow-kpi-${code}`} />
          ))}
        </div>

        {/* Filtres */}
        <section className="afr-card" style={{ padding: 12, marginBottom: 12 }}>
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
            <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }} data-testid="admin-escrow-status-filter">
              <button className={`afr-btn afr-btn--sm ${!status ? "afr-btn--primary" : "afr-btn--outline"}`}
                      onClick={() => setStatus("")}
                      data-testid="admin-escrow-filter-all">Tous</button>
              {Object.keys(STATUS_LABELS).map((s) => (
                <button
                  key={s}
                  className={`afr-btn afr-btn--sm ${status === s ? "afr-btn--primary" : "afr-btn--outline"}`}
                  onClick={() => setStatus(s)}
                  data-testid={`admin-escrow-filter-${s}`}
                >
                  {STATUS_LABELS[s]}
                </button>
              ))}
            </div>
            <div style={{ position: "relative", flex: "1 1 240px" }}>
              <Search size={14} style={{ position: "absolute", top: 9, left: 8, color: "#94a3b8" }} />
              <input
                value={q}
                onChange={(e) => setQ(e.target.value)}
                placeholder="Recherche : ID, client, talent, titre…"
                data-testid="admin-escrow-search"
                style={{ width: "100%", padding: "6px 8px 6px 28px", border: "1px solid #E2E8F0", borderRadius: 6 }}
              />
            </div>
          </div>
        </section>

        {/* Table */}
        <section className="afr-card" style={{ padding: 12 }} data-testid="admin-escrow-table-card">
          {loading ? (
            <div data-testid="admin-escrow-loading">Chargement…</div>
          ) : filtered.length === 0 ? (
            <div className="afr-empty" data-testid="admin-escrow-empty">
              <div className="afr-empty__title">Aucun séquestre pour ce filtre</div>
            </div>
          ) : (
            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: "0.85rem" }}>
                <thead>
                  <tr style={{ background: "#F1F5F9" }}>
                    <th style={th}>ID</th>
                    <th style={th}>Client</th>
                    <th style={th}>Talent</th>
                    <th style={th}>Montant</th>
                    <th style={th}>Plan</th>
                    <th style={th}>Statut</th>
                    <th style={th}>MAJ</th>
                    <th style={th}></th>
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((it) => (
                    <tr key={it.id} data-testid={`admin-escrow-row-${it.id}`}>
                      <td style={td}><code>{it.id.slice(0, 8)}</code></td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{it.company_name || "—"}</div>
                        <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{it.company_email}</div>
                      </td>
                      <td style={td}>
                        <div style={{ fontWeight: 600 }}>{it.talent_name || "—"}</div>
                        <div style={{ fontSize: "0.72rem", color: "#94a3b8" }}>{it.talent_email}</div>
                      </td>
                      <td style={td}>{it.amount.toLocaleString()} {it.currency}</td>
                      <td style={td}>{it.deposit_plan === "full" ? "100 %" : "50/50"}</td>
                      <td style={td}>
                        <StatusBadge status={it.status} />
                      </td>
                      <td style={{ ...td, fontSize: "0.72rem", color: "#64748b" }}>
                        {it.updated_at ? new Date(it.updated_at).toLocaleString() : "—"}
                      </td>
                      <td style={td}>
                        <Link to={`/escrow/${it.id}`} className="afr-btn afr-btn--outline afr-btn--sm"
                              data-testid={`admin-escrow-open-${it.id}`}>Ouvrir</Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </section>
      </div>
    </PrivateLayout>
  );
}

function ConfigPanel({ cfg, onSaved }) {
  const [channels, setChannels] = useState(cfg.channels || []);
  const [helpText, setHelpText] = useState(cfg.help_text || "");
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      await api.patch("/v1/admin/escrow/config", { channels, help_text: helpText });
      toast.success("Configuration MoMo enregistrée");
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };

  const patch = (idx, key, val) => {
    setChannels((cs) => cs.map((c, i) => (i === idx ? { ...c, [key]: val } : c)));
  };

  return (
    <section className="afr-card" style={{ padding: 16, marginBottom: 14, background: "#FAFAF7" }}
             data-testid="admin-escrow-config-panel">
      <h3 style={{ margin: "0 0 10px", fontSize: "1rem" }}>Coordonnées Mobile Money AFRICTAL</h3>
      <p style={{ fontSize: "0.82rem", color: "#64748b" }}>
        Ces coordonnées seront affichées à l'entreprise lors du dépôt. Laisser le numéro vide masque le canal.
      </p>
      {channels.map((c, idx) => (
        <div key={c.code} style={{ padding: 10, border: "1px solid #E2E8F0", borderRadius: 6, marginTop: 8 }}
             data-testid={`admin-escrow-cfg-${c.code}`}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
            <input value={c.label} onChange={(e) => patch(idx, "label", e.target.value)}
                   placeholder="Nom du canal"
                   data-testid={`admin-escrow-cfg-label-${c.code}`} style={inp} />
            <input value={c.number} onChange={(e) => patch(idx, "number", e.target.value)}
                   placeholder="Numéro MoMo (+225 …)"
                   data-testid={`admin-escrow-cfg-number-${c.code}`} style={inp} />
            <input value={c.holder_name} onChange={(e) => patch(idx, "holder_name", e.target.value)}
                   placeholder="Bénéficiaire"
                   data-testid={`admin-escrow-cfg-holder-${c.code}`} style={inp} />
          </div>
          <input value={c.instructions || ""} onChange={(e) => patch(idx, "instructions", e.target.value)}
                 placeholder="Instructions courtes (ex. mentionner la référence)"
                 data-testid={`admin-escrow-cfg-instructions-${c.code}`}
                 style={{ ...inp, marginTop: 8 }} />
        </div>
      ))}
      <textarea value={helpText} onChange={(e) => setHelpText(e.target.value)}
                rows={3} placeholder="Message général affiché à l'entreprise"
                data-testid="admin-escrow-cfg-help"
                style={{ ...inp, marginTop: 12, resize: "vertical" }} />
      <div style={{ display: "flex", justifyContent: "flex-end", marginTop: 10 }}>
        <button className="afr-btn afr-btn--primary afr-btn--sm" onClick={save} disabled={busy}
                data-testid="admin-escrow-cfg-save">
          {busy ? "…" : "Enregistrer"}
        </button>
      </div>
    </section>
  );
}

function Kpi({ label, value, status, testid }) {
  const color = {
    PENDING_PAYMENT: "#B45309", A_VERIFIER: "#78350F", HELD_IN_ESCROW: "#009640",
    IN_PROGRESS: "#1E40AF", DELIVERED: "#1E40AF", UNDER_REVIEW: "#1E40AF",
    DISPUTED: "#991B1B", RELEASED: "#065F46", COMPLETED: "#065F46",
  }[status] || "#0F2A42";
  return (
    <div className="afr-card" style={{ padding: 10, borderLeft: `3px solid ${color}` }} data-testid={testid}>
      <div style={{ fontSize: "0.72rem", color: "#64748b" }}>{label}</div>
      <div style={{ fontSize: "1.3rem", fontWeight: 700, color }}>{value}</div>
    </div>
  );
}

function StatusBadge({ status }) {
  const c = {
    PENDING_PAYMENT: ["#FEF3C7", "#78350F"],
    A_VERIFIER: ["#FEF3C7", "#78350F"],
    HELD_IN_ESCROW: ["#DCFCE7", "#065F46"],
    IN_PROGRESS: ["#DBEAFE", "#1E40AF"],
    DELIVERED: ["#DBEAFE", "#1E40AF"],
    UNDER_REVIEW: ["#DBEAFE", "#1E40AF"],
    DISPUTED: ["#FEE2E2", "#991B1B"],
    RELEASED: ["#DCFCE7", "#065F46"],
    COMPLETED: ["#DCFCE7", "#065F46"],
  }[status] || ["#E2E8F0", "#475569"];
  return (
    <span data-testid={`admin-escrow-badge-${status}`}
          style={{ padding: "3px 8px", borderRadius: 999, background: c[0], color: c[1], fontWeight: 700, fontSize: "0.7rem" }}>
      {STATUS_LABELS[status] || status}
    </span>
  );
}

const th = { textAlign: "left", padding: "8px 10px", fontWeight: 600, fontSize: "0.78rem" };
const td = { padding: "8px 10px", borderBottom: "1px solid #E2E8F0" };
const inp = { padding: "6px 8px", border: "1px solid #E2E8F0", borderRadius: 4, fontSize: "0.85rem", width: "100%" };
